/**
 * Mousai Commerce - scroll reveal
 *
 * Design notes:
 * - Everything is driven by CSS custom properties set in theme.liquid, so the
 *   merchant changes duration/distance/easing in the editor, not in code.
 * - Stagger is applied per group, not per page, so each section counts from 0.
 * - Reduced motion short-circuits before the observer is ever created.
 * - In the theme editor, sections that load after page load are re-scanned and
 *   revealed instantly, otherwise the merchant sees blank blocks while editing.
 */
(() => {
  const body = document.body;
  if (!body.classList.contains('anim-enabled')) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Reveal everything with no motion at all. */
  const revealAll = (root = document) => {
    root.querySelectorAll('[data-animate]').forEach((el) => {
      el.style.transition = 'none';
      el.classList.add('is-visible');
    });
  };

  if (reduced.matches) {
    revealAll();
    return;
  }

  const once = body.dataset.animateOnce !== 'false';
  const staggerMs =
    parseInt(getComputedStyle(document.documentElement).getPropertyValue('--anim-stagger'), 10) || 0;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          if (once) observer.unobserve(entry.target);
        } else if (!once) {
          entry.target.classList.remove('is-visible');
        }
      });
    },
    {
      // Fire slightly before the element reaches the fold so the motion reads
      // as the element arriving, rather than catching up after the fact.
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.01
    }
  );

  /**
   * Assign stagger delays within each animation group, then observe.
   * A group is any element with [data-animate-group]; elements outside a group
   * are treated as their own group of one.
   */
  const scan = (root = document) => {
    root.querySelectorAll('[data-animate-group]').forEach((group) => {
      group.querySelectorAll('[data-animate]').forEach((el, i) => {
        if (!el.style.getPropertyValue('--anim-delay')) {
          el.style.setProperty('--anim-delay', `${i * staggerMs}ms`);
        }
      });
    });

    root.querySelectorAll('[data-animate]:not(.is-visible)').forEach((el) => observer.observe(el));
  };

  // first scan runs in the next animation frame: at DOMContentLoaded the
  // layout is still dirty and the style read here forced a synchronous
  // layout of the whole page
  const start = () => requestAnimationFrame(() => scan());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // Theme editor: new or reordered sections should appear immediately.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => revealAll(e.target));
    document.addEventListener('shopify:section:reorder', () => revealAll());
    document.addEventListener('shopify:block:select', (e) => revealAll(e.target));
  }

  // If the visitor turns reduced motion on mid-session, respect it right away.
  reduced.addEventListener('change', (e) => {
    if (e.matches) {
      observer.disconnect();
      revealAll();
    }
  });
})();
